import React, { CSSProperties, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useScrollReveal } from '../../../hooks';
import { SectionHeader } from '../../../sections/ThesisSection/layouts';
import { useTheme } from '../../../theme';
import { DEMOS, Demo } from '../content';
import { rich } from '../rich';
import { Band, reveal } from './Band';

// The demo videos. On a wide screen the video holds still on the right while the
// descriptions scroll up past it on the left, and it changes to the demo whose description
// has come up beside it. On a narrow screen each description sits above its own video.

const HEADER = 72; // where main's floating header ends on a wide screen
const LINE = 0.6; // a description takes over once its top passes 60% of the screen

const pad = (n: number) => String(n).padStart(2, '0');

function useMedia(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const change = () => setMatches(list.matches);
    change();
    list.addEventListener('change', change);
    return () => list.removeEventListener('change', change);
  }, [query]);
  return matches;
}

// A video plays from the start each time it comes on show. With reduced motion it waits for
// the reader, and the one on show has controls. Only the clip and poster for the page's theme
// are fetched, so the other theme's never slow the page; when the theme changes, the clip
// changes with it and starts again. It offers no picture-in-picture or casting, whose buttons
// Edge and Chrome on Android would otherwise lay over the clip.
function DemoVideo({ demo, shown, playing, still }: { demo: Demo; shown: boolean; playing: boolean; still: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const { resolvedTheme } = useTheme();
  const clip = demo.video?.[resolvedTheme];
  const src = clip?.src;
  useEffect(() => {
    const video = ref.current;
    if (!video || still) return;
    if (playing) {
      video.currentTime = 0;
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [playing, still, src]);

  if (!clip) {
    return (
      <div className="oh-demo-empty" role="img" aria-label={`${demo.name} demo video`}>
        <span className="oh-demo-play" aria-hidden="true" />
        <span className="font-mono text-label-sm uppercase text-foreground-muted">{demo.name}</span>
      </div>
    );
  }
  return (
    <video
      ref={ref}
      className="oh-demo-video"
      src={clip.src}
      poster={clip.poster}
      muted
      loop
      playsInline
      disablePictureInPicture
      disableRemotePlayback
      preload="metadata"
      controls={still && shown}
      aria-label={`${demo.name} demo video`}
    />
  );
}

function DemoCopy({ demo, index }: { demo: Demo; index: number }) {
  return (
    <div className="oh-demo-copy">
      <span className="oh-demo-num">{pad(index + 1)}</span>
      <h3 className="font-display text-heading-lg md:text-heading text-foreground text-balance">{demo.title}</h3>
      <p className="oh-demo-name">{demo.name}</p>
      <p className="oh-demo-text mt-3 max-w-sm font-body text-body-sm text-foreground-tertiary leading-relaxed">{rich(demo.text)}</p>
    </div>
  );
}

// True while at least `share` of the element is on screen.
function useOnScreen(ref: RefObject<Element>, share: number) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const watch = new IntersectionObserver(([entry]) => setSeen(entry.isIntersecting), { threshold: share });
    watch.observe(el);
    return () => watch.disconnect();
  }, [ref, share]);
  return seen;
}

function WideDemos({ still }: { still: boolean }) {
  const grid = useRef<HTMLDivElement>(null);
  const pin = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const steps = useRef<(HTMLLIElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const [top, setTop] = useState(HEADER + 24);
  const seen = useOnScreen(frame, 0.25);

  // The description beside the video is the last one whose top has passed the line.
  useEffect(() => {
    let frameId = 0;
    const update = () => {
      frameId = 0;
      const line = window.innerHeight * LINE;
      let next = 0;
      steps.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= line) next = i;
      });
      setActive(next);
    };
    const schedule = () => {
      if (!frameId) frameId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  // The video and its names hold still in the middle of the screen below the header. The
  // last description is as tall as they are, so they all leave together.
  useLayoutEffect(() => {
    const held = pin.current;
    const g = grid.current;
    if (!held || !g) return undefined;
    const measure = () => {
      const height = held.offsetHeight;
      g.style.setProperty('--oh-pin-h', `${height}px`);
      setTop(Math.max(HEADER + 24, Math.round(HEADER + (window.innerHeight - HEADER - height) / 2)));
    };
    measure();
    const sizes = new ResizeObserver(measure);
    sizes.observe(held);
    window.addEventListener('resize', measure);
    return () => {
      sizes.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // A name under the video scrolls its description up beside it.
  const go = (i: number) => {
    const el = steps.current[i];
    if (!el) return;
    const y = window.scrollY + el.getBoundingClientRect().top - window.innerHeight * (LINE - 0.15);
    window.scrollTo({ top: y, behavior: still ? 'auto' : 'smooth' });
  };

  return (
    <div ref={grid} className="grid grid-cols-[5fr_7fr] gap-16">
      <ol className="oh-demo-steps">
        {DEMOS.map((demo, i) => (
          <li
            key={demo.id}
            ref={(el) => {
              steps.current[i] = el;
            }}
            className={`oh-demo-step ${i === active ? 'is-active' : ''}`}
          >
            <DemoCopy demo={demo} index={i} />
          </li>
        ))}
      </ol>
      <div>
        <div ref={pin} className="oh-demo-pin" style={{ top }}>
          <div ref={frame} className="oh-demo-frame">
            {DEMOS.map((demo, i) => (
              <div key={demo.id} className={`oh-demo-media ${i === active ? 'is-active' : ''}`} aria-hidden={i !== active}>
                <DemoVideo demo={demo} shown={i === active} playing={seen && i === active} still={still} />
              </div>
            ))}
          </div>
          <nav className="oh-demo-tabs" aria-label="Demos">
            {DEMOS.map((demo, i) => (
              <button
                key={demo.id}
                type="button"
                style={{ '--c': `hsl(var(--brand-${demo.colour}))` } as CSSProperties}
                aria-current={i === active ? 'true' : undefined}
                onClick={() => go(i)}
              >
                {demo.name}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

function NarrowDemo({ demo, index, still }: { demo: Demo; index: number; still: boolean }) {
  const frame = useRef<HTMLDivElement>(null);
  const seen = useOnScreen(frame, 0.6);
  return (
    <li>
      <DemoCopy demo={demo} index={index} />
      <div ref={frame} className="oh-demo-frame mt-8">
        <div className="oh-demo-media is-active">
          <DemoVideo demo={demo} shown playing={seen} still={still} />
        </div>
      </div>
    </li>
  );
}

export function DemosSection() {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
  const wide = useMedia('(min-width: 1024px)');
  const still = useMedia('(prefers-reduced-motion: reduce)');
  return (
    <Band id="demos">
      <div ref={ref as RefObject<HTMLDivElement>}>
        <SectionHeader
          label="Demos"
          title="Watch it decide."
          description="Short videos of agents making their choices with OneHot."
          isVisible={isVisible}
          labelColor="ink"
        />
      </div>
      <div className={reveal(isVisible)} style={{ animationDelay: '0.2s' }}>
        {wide ? (
          <WideDemos still={still} />
        ) : (
          <ol className="oh-demo-steps grid gap-16">
            {DEMOS.map((demo, i) => (
              <NarrowDemo key={demo.id} demo={demo} index={i} still={still} />
            ))}
          </ol>
        )}
      </div>
    </Band>
  );
}

export default DemosSection;
